import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { toast } from "sonner";

function formatDate(iso, lang) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(lang === "es" ? "es-ES" : "en-US", {
      day: "2-digit", month: "long", year: "numeric",
    });
  } catch { return iso; }
}

const STATUS_BADGE = {
  pending: { bg: "#FFC53D", color: "#1C1917", label: "billing_req_pending" },
  approved: { bg: "#46A758", color: "#FFFFFF", label: "billing_req_approved" },
  rejected: { bg: "#E5484D", color: "#FFFFFF", label: "billing_req_rejected" },
};

export default function Billing() {
  const { t, i18n } = useTranslation();
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [search] = useSearchParams();
  const [status, setStatus] = useState(null);
  const [requests, setRequests] = useState([]);
  const [planId, setPlanId] = useState("monthly");
  const [txnId, setTxnId] = useState("");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const awaiting = search.get("awaiting") === "1";

  async function refresh() {
    try {
      const [s, r] = await Promise.all([
        api.get("/billing/status"),
        api.get("/billing/my-requests"),
      ]);
      setStatus(s.data);
      setRequests(r.data.requests || []);
    } catch {}
  }

  useEffect(() => { refresh(); }, []);

  async function submit() {
    const t2 = txnId.trim();
    if (!t2) {
      toast.error(t("billing_req_txn_empty"));
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/billing/payment-request", {
        plan_id: planId,
        paypal_txn_id: t2,
        note,
      });
      toast.success(t("billing_req_submitted"));
      setTxnId("");
      setNote("");
      await refresh();
      try {
        const me = await api.get("/auth/me");
        setUser(me.data);
      } catch {}
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSubmitting(false);
    }
  }

  if (!status) return null;

  const isTrialing = status.effective_status === "trialing";
  const isActive = status.effective_status === "active";
  const expiry = isActive ? status.active_until : status.trial_end_at;
  const hasPending = requests.some((r) => r.status === "pending");

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-[#FDFBF7]" data-testid="billing-page">
      <div className="max-w-3xl mx-auto px-6 md:px-10 py-12">
        <span className="eyebrow text-xs">{t("billing_my_plan")}</span>
        <h1 className="mt-2 hero-display text-4xl sm:text-5xl text-[#1C1917]">{user?.name || user?.email}</h1>

        {(awaiting || hasPending) && !isActive && (
          <div className="mt-6 border-2 border-[#1C1917] bg-[#FFC53D] p-5 shadow-[4px_4px_0_0_#1C1917]" data-testid="awaiting-banner">
            <div className="font-black text-sm text-[#1C1917]">{t("billing_awaiting_title")}</div>
            <p className="text-xs text-[#1C1917] mt-1 leading-relaxed">{t("billing_awaiting_sub")}</p>
          </div>
        )}

        {/* Status card */}
        <div className="mt-8 border-2 border-[#1C1917] bg-white p-8 shadow-[6px_6px_0_0_#1C1917]" data-testid="billing-status-card">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <span className={`inline-block px-3 py-1 text-[10px] font-black tracking-widest uppercase border-2 border-[#1C1917] ${
                isActive ? "bg-[#46A758] text-white" : isTrialing ? "bg-[#FFC53D] text-[#1C1917]" : "bg-[#E5484D] text-white"
              }`}>
                {isActive ? t("billing_status_active") : isTrialing ? t("billing_status_trialing") : t("billing_status_expired")}
              </span>
              {isActive && status.plan_type && (
                <p className="mt-3 text-sm text-[#57534E] font-bold">
                  {status.plan_type === "annual" ? t("billing_plan_annual") : t("billing_plan_monthly")}
                </p>
              )}
              <p className="mt-2 text-base text-[#1C1917]">
                {expiry ? t("billing_active_until", { date: formatDate(expiry, i18n.language) }) : "—"}
              </p>
              {(isTrialing || isActive) && status.days_left > 0 && (
                <p className="mt-1 text-sm text-[#57534E]">{t("billing_days_left", { days: status.days_left })}</p>
              )}
            </div>
            {!isActive && (
              <Button
                onClick={() => navigate("/pricing")}
                className="rounded-none border-2 border-[#1C1917] bg-[#E5484D] text-white hover:bg-[#CE2C31] font-black shadow-[3px_3px_0_0_#1C1917] hover:shadow-[5px_5px_0_0_#1C1917]"
                data-testid="btn-go-pricing"
              >
                {t("paywall_cta")}
              </Button>
            )}
          </div>
        </div>

        {/* Submit payment claim */}
        <div className="mt-8 border-2 border-[#1C1917] bg-white p-8 shadow-[6px_6px_0_0_#1C1917]" data-testid="claim-card">
          <span className="eyebrow text-xs">{t("billing_claim_eyebrow")}</span>
          <h2 className="display text-2xl mt-1 text-[#1C1917]">{t("billing_claim_title")}</h2>
          <p className="text-sm text-[#57534E] mt-2 max-w-xl leading-relaxed">{t("billing_claim_help")}</p>

          <div className="mt-6 grid sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <Label className="eyebrow text-xs">{t("billing_claim_plan")}</Label>
              <div className="mt-1 grid grid-cols-2 gap-2">
                {[
                  { id: "monthly", label: t("billing_plan_monthly") + " — 6,99 €" },
                  { id: "annual", label: t("billing_plan_annual") + " — 49,99 €" },
                ].map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => setPlanId(p.id)}
                    className={`h-12 border-2 border-[#1C1917] text-xs font-black tracking-wider uppercase transition-all ${
                      planId === p.id ? "bg-[#1C1917] text-[#FDFBF7] shadow-[3px_3px_0_0_#FFC53D]" : "bg-white text-[#1C1917] hover:bg-[#FFC53D]"
                    }`}
                    data-testid={`claim-plan-${p.id}`}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="sm:col-span-2">
              <Label className="eyebrow text-xs">{t("billing_claim_txn")}</Label>
              <input
                value={txnId}
                onChange={(e) => setTxnId(e.target.value)}
                placeholder={t("billing_claim_txn_placeholder")}
                className="mt-1 w-full h-12 px-3 border-2 border-[#1C1917] bg-white text-[#1C1917] placeholder:text-[#A8A29E] focus:outline-none focus:shadow-[3px_3px_0_0_#1C1917] transition-shadow font-mono text-sm"
                data-testid="claim-txn-input"
              />
            </div>
            <div className="sm:col-span-2">
              <Label className="eyebrow text-xs">{t("billing_claim_note_optional")}</Label>
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={2}
                placeholder={t("billing_claim_note_placeholder")}
                className="mt-1 w-full px-3 py-2 border-2 border-[#1C1917] bg-white text-[#1C1917] placeholder:text-[#A8A29E] focus:outline-none focus:shadow-[3px_3px_0_0_#1C1917] transition-shadow text-sm"
                data-testid="claim-note-input"
              />
            </div>
          </div>
          <Button
            onClick={submit}
            disabled={submitting || !txnId.trim()}
            className="mt-6 w-full sm:w-auto rounded-none h-12 px-8 border-2 border-[#1C1917] bg-[#1C1917] text-[#FDFBF7] hover:bg-black font-black tracking-widest uppercase text-sm shadow-[3px_3px_0_0_#1C1917] hover:shadow-[5px_5px_0_0_#1C1917] hover:-translate-y-[1px] transition-all"
            data-testid="btn-claim-submit"
          >
            {submitting ? t("billing_claim_submitting") : t("billing_claim_submit")}
          </Button>
        </div>

        {/* History */}
        {requests.length > 0 && (
          <div className="mt-8 border-2 border-[#1C1917] bg-white p-8 shadow-[6px_6px_0_0_#1C1917]" data-testid="claim-history">
            <span className="eyebrow text-xs">{t("billing_history")}</span>
            <h3 className="display text-xl mt-1 text-[#1C1917]">{t("billing_history_title")}</h3>
            <div className="mt-4 space-y-3">
              {requests.map((r) => {
                const s = STATUS_BADGE[r.status] || STATUS_BADGE.pending;
                return (
                  <div key={r.id} className="border-2 border-[#1C1917] p-4 flex items-start justify-between gap-4 flex-wrap" data-testid={`req-${r.id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-mono text-xs text-[#1C1917] truncate" title={r.paypal_txn_id}>
                          {r.paypal_txn_id}
                        </span>
                        <span className="text-xs font-bold uppercase text-[#57534E]">
                          · {r.plan_id === "annual" ? "Anual" : "Mensual"}
                        </span>
                      </div>
                      <div className="text-xs text-[#57534E] mt-1">{formatDate(r.created_at, i18n.language)}</div>
                    </div>
                    <span
                      className="inline-block px-3 py-1 text-[10px] font-black tracking-widest uppercase border-2 border-[#1C1917]"
                      style={{ background: s.bg, color: s.color }}
                    >
                      {t(s.label)}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
